import React, { useState } from 'react';
import isNumeric from 'validator/es/lib/isNumeric';
import { toFlag } from 'pano-utils';
import { useStore } from 'effector-react';
import { useQuery } from 'react-query';
import { type EditConfigVipView, type GeneralID, ImageType } from '@types';
import { API_DOMAIN, HOST } from '@constants';
import Title from '@components/Title';
import { Collapse } from '@components/Collapse';
import useTourEditing from '@hooks/useTourEditing';
import Input from '@components/Input';
import { Validator } from '@components/Validator';
import Tip from '@components/Tip';
import Button from '@components/Button/Button';
import TextLink from '@components/TextLink';
import showSceneModal from '@modules/category/SceneModal/showSceneModal';
import { $sceneMap } from '@stores/dataStores';
import Image from '@components/Image';
import IconTrash from '@components/icons/IconTrash';
import axios from '@utils/axios';
import PanelBase from '../PanelBase';
import type { PanelBaseProps } from '../types';
import MainToggle from '../components/MainToggle';

const MAX = 999;

type Identity = Partial<{
  cardNum: string;
}>;
type Bank = Partial<{
  /** "0:审核中 1:审核成功 2:审核失败", */
  status: 0 | 1 | 2;
}>;

function VipViewBase({
  defaultValue,
  onConfirm,
}: PanelBaseProps<EditConfigVipView>) {
  const sceneMap = useStore($sceneMap);
  const { state, setState, isChanged } = useTourEditing(defaultValue, {
    enabled: 0,
    sceneList: '',
  });
  const [payment, setPayment] = useState<string>(
    state.payment ? String(state.payment) : ''
  );

  const [list, setList] = useState<GeneralID[]>(() => {
    const idString = state.sceneList ?? '';
    if (!idString) return [];
    return idString.split(',').map((id) => {
      if (isNumeric(id)) return Number(id);
      return id;
    });
  });

  const info = useQuery(['vip-view-info'], async () => {
    const { cardNum } = await axios.get<Identity>(
      `${API_DOMAIN}/member/my/identity`
    );
    const { status } = await axios.get<Bank>(`${API_DOMAIN}/member/my/bank`);
    return { cardNum, status };
  });

  const handleConfirm = () => {
    if (state.enabled === 0) {
      onConfirm({ enabled: 0 });
      return;
    }
    if (!cardNum || status !== 1) {
      onConfirm({ enabled: 0 });
      return;
    }
    onConfirm({
      ...state,
      payment: Number(payment),
      sceneList: list.join(','),
    });
  };

  const handleAddScene = async () => {
    const { idSet } = await showSceneModal({
      defaultSelected: new Set(list),
      allowEmpty: true,
      multiple: true,
      title: '选择付费场景',
      subTitle: '建议至少选择3个及以上比较优质的场景作为付费观看场景',
    });
    if (!idSet) return;
    setList(Array.from(idSet));
  };

  if (info.isLoading || !info.data) return null;
  const { cardNum, status } = info.data;

  const renderList = () => {
    return (
      <div className="mt-10">
        {list.map((id, i) => {
          const item = sceneMap.get(id);
          if (!item) return null;
          return (
            <div
              key={item.id}
              className="relative mt-10 flex h-35 select-none items-center rounded bg-black-#34 px-10"
            >
              <Image
                src={item.thumbUrl}
                size={16}
                type={ImageType.thumb}
                className="h-16 w-16 bg-black"
              />
              <div className="mx-5 flex-grow truncate text-base text-white">
                {item.name}
              </div>
              <IconTrash
                className="flex-shrink-0"
                onClick={() => {
                  const newList = [...list];
                  newList.splice(i, 1);
                  setList(newList);
                }}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderSettings = () => {
    return (
      <>
        <Collapse title="付费金额">
          <div className="flex items-center">
            <Validator
              value={payment}
              validFn={(value) => {
                if (!value) return false;
                if (!isNumeric(value)) return false;
                const num = Number(value);
                return num > 0 && num <= MAX;
              }}
              warningMessage={`请输入付费金额, 不超过${MAX}元`}
            >
              {({ value, warning }) => (
                <Input
                  scheme="dark"
                  className="h-30 w-full"
                  placeholder={`现金金额不超过${MAX}`}
                  value={value}
                  warning={warning}
                  onClear={() => setPayment('')}
                  onChange={(e) => setPayment(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value.trim();
                    if (isNumeric(val) && Number(val) > 0) {
                      setPayment(String(Math.round(Number(val) * 100) / 100));
                    } else {
                      setPayment('');
                    }
                  }}
                />
              )}
            </Validator>
            <div className="ml-10 shrink-0">元</div>
          </div>
          <Tip className="mt-10">
            注：用户端支付该金额即可解锁查看所有场景，创作者可在订单管理-作品观看收益中查看收益详情并提现
          </Tip>
        </Collapse>
        <Collapse title="付费场景设置">
          <Button className="w-full" onClick={handleAddScene}>
            添加付费场景
          </Button>
          <Validator
            value={list.length}
            validFn={(value) => value > 0}
            warningMessage="请添加付费场景"
          >
            {({ value }) => (
              <>
                {value > 0 && renderList()}
                {value === 0 && (
                  <Tip className="mt-10 text-orange">
                    使用该功能必须确保您对当前作品中所有内容拥有绝对版权，且表示您已阅读并同意
                    <TextLink
                      href="https://www.720yun.com/bbs/article?id=1424"
                      target="_blank"
                    >
                      《720云内容付费观看协议》
                    </TextLink>
                  </Tip>
                )}
              </>
            )}
          </Validator>
        </Collapse>
      </>
    );
  };

  const renderContent = () => {
    if (!cardNum) {
      return (
        <div className="rounded bg-black-#28 px-10 py-15">
          <Button
            className="w-full"
            as="a"
            href={`${HOST}/my/admin/base/realname`}
            target="_blank"
          >
            去认证
          </Button>
          <Tip className="mt-10 text-orange">
            该功能需要实名认证并绑定银行卡后方可使用（以便后续获取收益后打款转账）
          </Tip>
        </div>
      );
    }
    if (status !== 1) {
      return (
        <div className="rounded bg-black-#28 px-10 py-15">
          <Button
            className="w-full"
            as="a"
            href={`${HOST}/my/admin/base/bank`}
            target="_blank"
          >
            去绑定
          </Button>
          <Tip className="mt-10 text-orange">
            该功能需要实名认证并绑定银行卡后方可使用（以便后续获取收益后打款转账）
          </Tip>
        </div>
      );
    }
    return renderSettings();
  };

  return (
    <PanelBase
      title={<Title>付费观看设置</Title>}
      isChanged={isChanged}
      onHelpClick={() => {}}
      onConfirm={handleConfirm}
    >
      <MainToggle
        isOn={!!state?.enabled}
        onToggle={(bool) => {
          setState((draft) => {
            draft.enabled = toFlag(bool);
          });
        }}
      />
      {!!state?.enabled && renderContent()}
    </PanelBase>
  );
}

export default VipViewBase;
